import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Route F7's links through the router instead of reloading the page.
 *
 * F7 is built for Next.js, and the vendored bundle replaces `next/link` with a
 * design-sync shim that renders a PLAIN `<a href>`:
 *
 *     const NextLink = forwardRef(function NextLink2({ href, ... }, ref) =>
 *       React.createElement("a", { ref, href: resolved, ...rest }))
 *
 * So every `href` prop in the kit — TableRow, Button, SidebarItem, NavbarItem,
 * DropdownItem, TextLink — is a full document navigation.
 *
 * In this console that is not merely slow, it signs the user OUT. Tokens live
 * in memory and nowhere else (docs/ARCHITECTURE.md, and asserted by
 * memoryStore.test.ts), so a document load discards them and drops the operator
 * back at the sign-in screen. Clicking a merchant row would end the session.
 *
 * Rather than banning `href` across the kit — which would cost real links,
 * middle-click, and "copy link address" — this intercepts the click once, at
 * the document, and hands same-origin navigations to the router. Every F7
 * component is fixed by it, including ones added later.
 *
 * Deliberately NOT intercepted, because each is a real document navigation the
 * user asked for: modified clicks (⌘/ctrl/shift/alt — open in a tab), any
 * non-primary button, `target` set, `download`, and anything off-origin.
 */
export function useInternalLinks() {
  const navigate = useNavigate();

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented) return;
      if (e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const anchor = (e.target as Element | null)?.closest?.("a");
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#")) return;
      if (anchor.hasAttribute("download")) return;
      // An explicit target (_blank and friends) is a deliberate new context.
      const target = anchor.getAttribute("target");
      if (target && target !== "_self") return;

      const url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin) return;

      e.preventDefault();
      navigate(url.pathname + url.search + url.hash);
    };

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [navigate]);
}
